/**
 * Deliver gate — when may a finished job's vehicle be released?
 *
 * Check Out is a money event; Deliver is a custody event. The vehicle leaves only
 * once the money side has reached a defined resting point:
 *
 *   dealer — a real invoice exists (generated OR paid). The shop bills the dealer
 *            and carries the receivable, so releasing against an open invoice is an
 *            extension of credit the shop chooses to make.
 *   retail — the invoice is paid. The vehicle is the leverage.
 *   either — the sale was settled offline. Cash or a check taken while Stripe was
 *            unreachable, so no invoice exists yet but the money is in the till.
 *            Holding a finished car hostage to a Stripe outage is worse than the
 *            paperwork arriving late, and it arrives late by design: the server
 *            materializes the invoice on a reconcile pass. See spec §2.
 *
 * The dealer rule is deliberately billing-model agnostic: it holds identically for
 * `direct` and `destination` jobs. The client never sees `charge_model` and must not
 * branch on it — reconciling the two models is the server's job. See
 * `tasks/deliver-flow-spec.md` §2 and §7.1.
 *
 * ZERO IMPORTS BY DESIGN — this module is unit-tested directly by
 * `scripts/test/deliverGate.test.mjs` under Node's ESM syntax detection.
 * Adding any import breaks that harness.
 */

export const DEALER = 'dealer';

/**
 * A real, materialized Stripe invoice — not a local draft. Drafts leave
 * `state.invoice` as `{}` and populate `state.draftPayment` instead, so an `id`
 * is what separates "invoice exists" from "we have a draft we could materialize".
 */
export function hasRealInvoice(invoice) {
  return Boolean(invoice && invoice.id);
}

export function isInvoicePaid(invoice) {
  return hasRealInvoice(invoice) && Boolean(invoice.paid);
}

/**
 * The `{ settledOffline: true, ... }` shape from `GET /invoices/draft/:requestId`,
 * returned when the request's latest live payment row is `paid` with no invoice —
 * i.e. cash or a check taken through the offline fallback.
 *
 * Read strictly: a truthy string or `1` is not consent, mirroring how the server
 * reads `deliverOnAccount`. This decides whether a vehicle leaves the shop, so the
 * signal has to be unambiguous.
 */
export function isSettledOffline(offlineSettlement) {
  return Boolean(offlineSettlement) && offlineSettlement.settledOffline === true;
}

/**
 * @param {object} request the request row; only `requestType` is read
 * @param {object} invoice the Stripe invoice in state, or `{}` when none/draft
 * @param {object} [offlineSettlement] the settled-offline payload, when present
 * @returns {boolean} whether Deliver should be offered
 */
export function canDeliver(request, invoice, offlineSettlement) {
  if (!request) return false;

  // Money in the till outranks both invoice rules — it is strictly stronger
  // evidence than the dealer's "an invoice exists" and satisfies retail's "paid".
  if (isSettledOffline(offlineSettlement)) return true;

  return request.requestType === DEALER
    ? hasRealInvoice(invoice)
    : isInvoicePaid(invoice);
}
